import React, {useState} from "react";
import {OperationVariables, QueryResult, useQuery} from "@apollo/client";
import {graphQLServices} from "../../services/GraphQLServices";
import {
	Box,
	Breadcrumbs,
	CircularProgress,
	Typography
} from "@mui/material";
import {deepClone, normalizeData} from "../../libraries/util/Functions";
import {PentaSplit} from "../../components/common/PentaSplit";
import {Link} from "react-router-dom";
import {pageAuthorizedByGroup} from "../fragments/PageAuthorization";
import {
	ANALYZE_CHANGES,
	DELETE_PHRASES_ITEM_AND_INHERITED,
	DELETE_TRANSLATION_ITEMS,
	EXISTS_MODULE_BY_ID,
	EXISTS_PHRASE_BY_ID, GET_MODULES,
	PUT_MODULE,
	PUT_PHRASE_ITEM,
	PUT_TRANSLATION_ITEM,
	
} from "./Queries";
import {DifferenceSet} from "./models/DifferenceSet";
import {DeployDisplay} from "../../components/common/deploy/DeployDisplay";
import {Module} from "./models/Module";
import {PhraseItem} from "./models/PhraseItem";
import {TranslationItem} from "./models/TranslationItem";
import {openModuleDetailsFor} from "./ModuleDetails";
import {openPhraseItemDetailsFor} from "./PhraseItemDetails";
import {openTranslationItemDetailsFor} from "./TranslationItemDetails";
import {cloneMutationData} from "../products/Utils";
import {translateFunction} from "../../localization/TranslationService";
import {ProcessButton} from "../../components/common/input/ProcessButton";
import {translationsEditorTargetStage} from "./TranslationsOverview";

const translate = translateFunction("SureBasePortalTranslations");

async function putModule(stage: string, item: Module) {
	await graphQLServices.translationsClient.mutate({
		mutation: PUT_MODULE,
		variables: {
			stage: stage,
			module: cloneMutationData(item)
		}
	});
}

async function moduleExists(stage: string, id: string): Promise<boolean> {
	
	const existsItems = await graphQLServices.translationsClient.query(
		{
			query: EXISTS_MODULE_BY_ID,
			variables: {
				stage: stage,
				id: id,
			},
			fetchPolicy: "no-cache"
		}
	);
	
	const nodes: any[] = existsItems.data.modules.nodes;
	return nodes.filter(x => x.id === id).length > 0;
}

async function deleteModule(stage: string, itemId: string) {
	// todo
}

function openNewModule(id: string) {
	// todo
}

async function putPhraseItem(stage: string, item: PhraseItem) {
	await graphQLServices.translationsClient.mutate({
		mutation: PUT_PHRASE_ITEM,
		variables: {
			stage: stage,
			phrase: cloneMutationData(item),
		}
	});
}

async function phraseExists(stage: string, id: string): Promise<boolean> {
	
	const existsItems = await graphQLServices.translationsClient.query(
		{
			query: EXISTS_PHRASE_BY_ID,
			variables: {
				stage: stage,
				id: id,
			},
			fetchPolicy: "no-cache"
		}
	);
	
	const nodes: any[] = existsItems.data.phraseItems.nodes;
	return nodes.filter(x => x.id === id).length > 0;
}

async function deletePhraseItem(stage: string, itemId: string) {
	await graphQLServices.translationsClient.mutate({
		mutation: DELETE_PHRASES_ITEM_AND_INHERITED,
		variables: {
			stage: stage,
			phraseIds: [itemId]
		}
	});
}

function openNewPhraseItem(id: string) {
	// todo
}

async function putTranslationItem(stage: string, item: TranslationItem) {
	await moduleExists(stage, item.moduleId).then(async (x) => {
		if (!x) {
			throw new Error(`Module does not exists on ${stage}`);
		}
		await phraseExists(stage, item.phraseId).then(async (y) => {
			if (!y) {
				throw new Error(`Phrase does not exists on ${stage}`);
			}
			await graphQLServices.translationsClient.mutate({
				mutation: PUT_TRANSLATION_ITEM,
				variables: {
					stage: stage,
					translation: cloneMutationData(item),
				}
			});
		});
	});
}

async function deleteTranslation(stage: string, itemId: string) {
	await graphQLServices.translationsClient.mutate({
		mutation: DELETE_TRANSLATION_ITEMS,
		variables: {
			stage: stage,
			translationItemIds: [itemId]
		}
	});
}

function openNewTranslation(id: string) {
	// todo
}

async function applyAll(differenceSet: DifferenceSet) {
	for (const itemArray of differenceSet.modules) {
		for (let i = 1; i < itemArray.length; i++) {
			const item = itemArray[i];
			const previousItem = itemArray[i - 1];
			if (!item && previousItem) {
				await putModule(differenceSet.stages[i], previousItem);
				itemArray[i] = previousItem;
			} else if (item && previousItem && jsonFormat(item) !== jsonFormat(previousItem)) {
				await putModule(differenceSet.stages[i], previousItem);
				itemArray[i] = previousItem;
			}
		}
	}
	
	for (const itemArray of differenceSet.phraseItems) {
		for (let i = 1; i < itemArray.length; i++) {
			const item = itemArray[i];
			const previousItem = itemArray[i - 1];
			if (!item && previousItem) {
				await putPhraseItem(differenceSet.stages[i], previousItem);
				itemArray[i] = previousItem;
			} else if (item && previousItem && jsonFormat(item) !== jsonFormat(previousItem)) {
				await putPhraseItem(differenceSet.stages[i], previousItem);
				itemArray[i] = previousItem;
			} else if (!previousItem && item) {
				await deletePhraseItem(differenceSet.stages[i], item.id);
				itemArray[i] = previousItem;
			}
		}
	}
	
	for (const itemArray of differenceSet.translationItems) {
		for (let i = 1; i < itemArray.length; i++) {
			const item = itemArray[i];
			const previousItem = itemArray[i - 1];
			if (!item && previousItem) {
				await putTranslationItem(differenceSet.stages[i], previousItem);
				itemArray[i] = previousItem;
			} else if (item && previousItem && jsonFormat(item) !== jsonFormat(previousItem)) {
				await putTranslationItem(differenceSet.stages[i], previousItem);
				itemArray[i] = previousItem;
			} else if (!previousItem && item) {
				await deleteTranslation(differenceSet.stages[i], item.id);
				itemArray[i] = previousItem;
			}
		}
	}
}

function jsonFormat(value: any): string {
	return JSON.stringify(value, null, 4);
}

export const TranslationsChangesOverview = pageAuthorizedByGroup(function () {
	return <PentaSplit
		top={<Breadcrumbs style={{margin: "5px"}}>
			<Link style={{color: "inherit", textDecoration: "unset"}}
				to="/translations_setup">{translate("Translations Setup")}</Link>
			<Typography color="text.primary">{translate("Changes")}</Typography>
		</Breadcrumbs>}
		center={<TranslationDifferences/>}
	/>;
}, ["Admin", "Translations"]);

export interface TranslationDifferencesProps {
	readonly variables?: any;
}

export function TranslationDifferences(props: TranslationDifferencesProps) {
	const [modulesLookup, setModulesLookup] = useState<Map<string, string>>(new Map());
	
	const query = useQuery(ANALYZE_CHANGES, {
		client: graphQLServices.translationsClient,
		fetchPolicy: "no-cache",
		variables: props.variables,
	});
	
	const moduleQuery = useQuery(GET_MODULES, {
		client: graphQLServices.translationsClient,
		fetchPolicy: "no-cache",
		variables: {
			stage: translationsEditorTargetStage,
			first: 10_000,
			order: [
				{name: "ASC"}
			]
		}
	});
	
	if (query.loading || moduleQuery.loading) {
		return <Box sx={{display: "flex", width: "100%", height: "100%"}}>
			<CircularProgress style={{margin: "auto"}}/>
		</Box>;
	}
	
	if (query.error || moduleQuery.error) {
		throw query.error;
	}
	
	if (!query.data || !moduleQuery.data) {
		throw new Error("Missing data!");
	}
	
	const differenceSet: DifferenceSet = normalizeData(deepClone(query.data.analyzeChanges));
	
	if (modulesLookup.size === 0) {
		const modMap: Map<string, string> = new Map();
		for (let i = 0; i < moduleQuery.data?.modules.nodes.length; i++) {
			const moduleValue = moduleQuery.data?.modules.nodes[i];
			modMap.set(moduleValue.id, moduleValue.name);
		}
		
		setModulesLookup(modMap);
	}
	
	async function handleApplyAll(differenceSet: DifferenceSet) {
		await applyAll(differenceSet);
		await query.refetch();
	}
	
	return <div style={{overflowY: "auto", height: "100%"}}>
		<ProcessButton
			variant={"contained"}
			defaultText={translate("Apply All")}
			inProgressText={translate("Applying...")}
			completedText={translate("Changes applied")}
			onClick={() => handleApplyAll(differenceSet)}
		/>
		{differenceSet.modules.length > 0 && <Typography>{translate("Modules")}</Typography>}
		{differenceSet.modules.map((set, index) => {
			return <DeployDisplay<Module>
				name={translate("Module")}
				key={`module-${index}`}
				stages={differenceSet.stages}
				items={set}
				keyMapper={it => it.id}
				labelMapper={it => translate(it.name)}
				putItem={putModule}
				deleteItem={deleteModule}
				openItemDetails={openModuleDetailsFor}
				openNewItemDetails={openNewModule}
			/>;
		})}
		{differenceSet.phraseItems.length > 0 && <Typography>{translate("Phrases")}</Typography>}
		{differenceSet.phraseItems.map((set, index) => {
			return <DeployDisplay<PhraseItem>
				name={translate("Phrase")}
				key={`phrase-${index}`}
				stages={differenceSet.stages}
				items={set}
				keyMapper={it => it.id}
				labelMapper={it => translate(it.phrase)}
				putItem={putPhraseItem}
				deleteItem={deletePhraseItem}
				openItemDetails={openPhraseItemDetailsFor}
				openNewItemDetails={openNewPhraseItem}
			/>;
		})}
		{differenceSet.translationItems.length > 0 && <Typography>{translate("Translations")}</Typography>}
		{differenceSet.translationItems.map((set, index) => {
			return <DeployDisplay<TranslationItem>
				name={translate("Translations")}
				key={`translations-${index}`}
				stages={differenceSet.stages}
				items={set}
				keyMapper={it => it.id}
				labelMapper={x => getTranslationItemLabel(x, modulesLookup)}
				putItem={putTranslationItem}
				deleteItem={deleteTranslation}
				openItemDetails={openTranslationItemDetailsFor}
				openNewItemDetails={openNewTranslation}
			/>;
		})}
	</div>;
}

function getTranslationItemLabel(item: TranslationItem, modulesLookup: Map<string, string>) {
	const moduleName = modulesLookup.get(item.moduleId);
	const moduleText = moduleName ? `(${moduleName}) ` : "";
	return `${moduleText}${item.languageCode} - ${item.translation}`;
}

